import { useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';

import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import {
  getCardRotation,
  getGridRotation,
  getCaption,
  getTitle,
  getImageUrl,
  getImageAlt,
  formatPolaroidDate,
  shouldUsePushpin,
  getContentWarning,
} from '@/lib/polaroid-utils';
import { WashiTape } from './WashiTape';
import { Pushpin } from './Pushpin';
import { ContentWarningOverlay } from './ContentWarningOverlay';
import { cn } from '@/lib/utils';

interface PolaroidCardProps {
  event: NostrEvent;
  /** 'feed' = large single-column, 'grid' = small 2-col (no author), 'feed-grid' = small 2-col with author */
  variant?: 'feed' | 'grid' | 'feed-grid';
  index?: number;
  /** Hide the date line from the polaroid caption area (used when date is shown as a background marker instead) */
  hideDate?: boolean;
  /** Tape variant number to avoid (prevents consecutive same-color tapes) */
  avoidTapeVariant?: number;
  onPhotoClick?: (event: NostrEvent) => void;
}

export function PolaroidCard({ event, variant = 'feed', index = 0, hideDate = false, avoidTapeVariant, onPhotoClick }: PolaroidCardProps) {
  const navigate = useNavigate();
  const imageUrl = getImageUrl(event);
  const altText = getImageAlt(event);
  // In the feed, only show the title on the polaroid — content goes in margin notecards.
  // In grid views, show the full caption (title or truncated content).
  const caption = variant === 'feed' ? (getTitle(event) ?? '') : getCaption(event);
  const date = formatPolaroidDate(event.created_at);
  const isLarge = variant === 'feed';
  const isGrid = variant === 'grid' || variant === 'feed-grid';
  const showAuthor = variant === 'feed' || variant === 'feed-grid';
  const rotation = isLarge ? getCardRotation(event.id) : getGridRotation(event.id);
  const usePushpin = isGrid && shouldUsePushpin(event.id);

  const contentWarning = getContentWarning(event);
  const [cwRevealed, setCwRevealed] = useState(false);
  const [cwPeeling, setCwPeeling] = useState(false);
  const hasCw = !!contentWarning && !cwRevealed;
  const showBlur = hasCw && !cwPeeling;

  const author = useAuthor(showAuthor ? event.pubkey : undefined);
  const displayName = author.data?.metadata?.name ?? genUserName(event.pubkey);
  const avatar = author.data?.metadata?.picture;

  if (!imageUrl) return null;

  const handlePhotoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPhotoClick?.(event);
  };

  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const npub = nip19.npubEncode(event.pubkey);
    navigate(`/${npub}`);
  };

  return (
    <div
      className="relative"
    >
      {/* The polaroid card */}
      <div
        className={cn(
          'relative bg-polaroid-white rounded-[3px] cursor-pointer',
          'transition-all duration-200 ease-out',
          'hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(45,27,78,0.15)]',
          'active:scale-[0.97] active:shadow-[0_1px_4px_rgba(45,27,78,0.1)]',
          isLarge
            ? 'p-3 pb-0 shadow-[0_3px_12px_rgba(45,27,78,0.12)]'
            : 'p-2 pb-0 shadow-[0_1px_6px_rgba(45,27,78,0.08)]',
        )}
        style={{ transform: `rotate(${rotation}deg)` }}
        onClick={handlePhotoClick}
      >
        {/* Tape or pushpin */}
        {usePushpin ? (
          <Pushpin eventId={event.id} />
        ) : (
          <WashiTape eventId={event.id} size={isLarge ? 'lg' : 'sm'} avoidVariant={avoidTapeVariant} />
        )}

        {/* Photo */}
        <div className={cn(
          'relative overflow-hidden bg-cream-dark aspect-[4/5]',
        )}>
          <img
            src={imageUrl}
            alt={altText}
            className={cn(
              'w-full h-full object-cover transition-all duration-300 hover:scale-[1.02]',
              showBlur && 'blur-lg',
            )}
            loading="lazy"
          />
          {hasCw && (
            <ContentWarningOverlay
              reason={contentWarning}
              eventId={event.id}
              variant="card"
              onReveal={() => {
                setCwPeeling(true);
                setCwRevealed(true);
              }}
            />
          )}
        </div>

        {/* Caption area — INSIDE the polaroid */}
        <div className={cn(
          isLarge ? 'px-1 pt-1.5 pb-2' : 'px-1 pt-1 pb-1.5',
        )}>
          {caption && (
            <p className={cn(
              'font-hand text-pol-text truncate',
              isLarge ? 'text-[17px] leading-tight' : 'text-sm leading-tight',
            )}>
              {caption}
            </p>
          )}
          {!hideDate && (
            <p className={cn(
              'font-hand font-bold text-pol-text-caption',
              isLarge ? 'text-sm' : 'text-xs',
            )}>
              {date}
            </p>
          )}

          {/* Author row — inside the polaroid */}
          {showAuthor && (
            <button
              onClick={handleAuthorClick}
              className="flex items-center gap-1.5 mt-1 group"
            >
              <div className={cn(
                'rounded-full overflow-hidden bg-cream-dark border border-pol-pink/30 flex-shrink-0',
                isLarge ? 'w-5 h-5' : 'w-4 h-4',
              )}>
                {avatar ? (
                  <img src={avatar} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-pol-pink/40 to-pol-lilac/40" />
                )}
              </div>
              <span className={cn(
                'text-pol-text-secondary group-hover:text-pol-text transition-colors truncate',
                isLarge ? 'text-[12px]' : 'text-[10px]',
              )}>
                {displayName}
              </span>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
